import { useState } from "react";
import { adminApi, imgOf, originalUrl } from "../lib";
import { Panel, In, Uploader, useAdmin, save, Check, StatusSelect, SampleBadge } from "./parts";

function Albums({ albums, reload }) {
  const [name, setName] = useState("");
  const add = async () => { if (!name.trim()) return; await save(() => adminApi.post("/c/albums", { name: name.trim(), description: "" }), "Album added"); setName(""); reload(); };
  const rename = async (a) => { const n = window.prompt("Album name", a.name); if (n && n.trim()) { await save(() => adminApi.put(`/c/albums/${a.id}`, { name: n.trim(), description: a.description, order: a.order }), "Renamed"); reload(); } };
  const del = async (a) => { if (window.confirm(`Delete the album “${a.name}”? Its photographs are kept, just unfiled.`)) { await save(() => adminApi.delete(`/c/albums/${a.id}`), "Album deleted"); reload(); } };
  return (
    <Panel title="Albums" testid="admin-albums">
      <div className="flex flex-wrap gap-2 mb-4">
        {(albums || []).map((a) => (
          <span key={a.id} className="border border-lou-line px-3 py-1.5 text-sm flex items-center gap-2" data-testid={`admin-album-${a.id}`}>
            {a.name}<button className="underline text-xs" onClick={() => rename(a)} data-testid={`admin-album-rename-${a.id}`}>rename</button>
            <button className="underline text-xs text-red-700" onClick={() => del(a)}>delete</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 max-w-md"><input className="lou-input text-sm !py-2" placeholder="New album name" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-album-new" />
        <button className="lou-btn !py-2 text-sm" onClick={add} data-testid="admin-album-add">Add</button></div>
    </Panel>
  );
}

function TrimPanel({ mediaId, initRotate = 0, initCrop, onDone }) {
  const [rotate, setRotate] = useState(initRotate);
  const [crop, setCrop] = useState(initCrop || { top: 0, right: 0, bottom: 0, left: 0 });
  const [busy, setBusy] = useState(false);
  const run = async (auto) => {
    setBusy(true);
    const r = await save(() => adminApi.post(`/media/${mediaId}/reprocess`, { rotate, crop, auto }), auto ? "Edges detected and trimmed" : "Photo updated");
    setBusy(false);
    if (r) { setCrop(r.crop); onDone(); }
  };
  return (
    <div className="bg-lou-ivory border border-lou-line p-3 mt-2 space-y-3 text-sm" data-testid={`trim-panel-${mediaId}`}>
      <p className="text-xs text-lou-soft">Only the copy shown on the site changes — the original file is always kept as it was.</p>
      <div className="flex flex-wrap items-center gap-2">
        <span>Rotate:</span>
        {[0, 90, 180, 270].map((d) => (
          <button key={d} type="button" onClick={() => setRotate(d)} data-testid={`trim-rotate-${d}`}
            className={`px-2 py-1 border text-xs ${rotate === d ? "bg-lou-ink text-white" : "border-lou-line"}`}>{d === 0 ? "None" : `${d}° clockwise`}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {["top", "right", "bottom", "left"].map((k) => (
          <label key={k} className="block"><span className="text-xs capitalize">Trim {k}: {crop[k]}%</span>
            <input type="range" min={0} max={20} step={0.5} value={crop[k]} onChange={(e) => setCrop({ ...crop, [k]: Number(e.target.value) })}
              className="w-full accent-[#4A4592]" data-testid={`trim-${k}`} /></label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="lou-btn-ghost !py-1.5 text-xs" disabled={busy} onClick={() => run(true)} data-testid="trim-auto">Auto-detect scanned edges</button>
        <button type="button" className="lou-btn !py-1.5 text-xs" disabled={busy} onClick={() => run(false)} data-testid="trim-apply">{busy ? "Working…" : "Apply rotate & trim"}</button>
      </div>
    </div>
  );
}

function PhotoRow({ p, albums, reload }) {
  const [e, setE] = useState(null);
  const [trim, setTrim] = useState(false);
  const [v, setV] = useState(0);
  const patch = async (body, msg) => { await save(() => adminApi.patch(`/photos/${p.id}`, body), msg); reload(); };
  return (
    <div className="border border-lou-line p-3 flex gap-4" data-testid={`admin-photo-${p.id}`}>
      <img src={`${imgOf(p, true)}${v ? `&v=${v}` : ""}`} alt="" className="w-28 h-28 object-contain bg-lou-ivory shrink-0" />
      <div className="flex-1 min-w-0 text-sm space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusSelect value={p.status} onChange={(s) => patch({ status: s }, `Marked ${s}`)} testid={`admin-photo-status-${p.id}`} />
          <Check label="Featured" checked={p.featured} onChange={(v) => patch({ featured: v })} testid={`admin-photo-feature-${p.id}`} />
          <span className={`text-xs px-2 py-0.5 ${p.source === "visitor" ? "bg-lou-sage/15 text-lou-sage" : "bg-lou-line"}`}>{p.source === "visitor" ? "Visitor contribution" : "Family"}</span>
          <SampleBadge show={p.is_sample} />
        </div>
        <p className="truncate">{p.caption || <em className="text-lou-soft">No caption</em>}</p>
        <p className="text-xs text-lou-soft">Shared by {p.uploader_name}{p.uploader_email ? ` (${p.uploader_email})` : ""} · Photographer: {p.photographer_credit || "unknown"} · chose {p.visibility_choice}</p>
        {e ? (
          <div className="grid md:grid-cols-2 gap-3 pt-2">
            {["caption", "photographer_credit", "date_text", "location", "people"].map((k) => <In key={k} label={k.replace("_", " ")} value={e[k]} onChange={(v) => setE({ ...e, [k]: v })} testid={`admin-photo-${k}`} />)}
            <label className="block"><span className="lou-label">Album</span>
              <select className="lou-input text-sm !py-2" value={e.album_id || ""} onChange={(x) => setE({ ...e, album_id: x.target.value || null })}>
                <option value="">Unfiled</option>{(albums || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
            <div className="md:col-span-2"><In label="Story" textarea rows={3} value={e.story} onChange={(v) => setE({ ...e, story: v })} /></div>
            <div className="flex gap-3"><button className="lou-btn !py-2 text-sm" onClick={async () => { await patch(e); setE(null); }} data-testid="admin-photo-save">Save</button>
              <button className="lou-btn-ghost text-sm" onClick={() => setE(null)}>Cancel</button></div>
            {p.original && <p className="md:col-span-2 text-xs text-lou-soft">Original submission kept: “{p.original.caption || "—"}” {p.original.people ? `· pictured: ${p.original.people}` : ""}</p>}
          </div>
        ) : (
          <div className="flex gap-3"><button className="underline" onClick={() => setE({ ...p })} data-testid={`admin-photo-edit-${p.id}`}>Edit details</button>
            {p.media_id && <button className="underline" onClick={() => setTrim(!trim)} data-testid={`admin-photo-trim-${p.id}`}>Rotate & trim</button>}
            {p.media_id && <a className="underline" href={originalUrl(p.media_id)}>Download original</a>}
            {p.media_id && <button className="underline" data-testid={`admin-photo-door-${p.id}`}
              onClick={() => save(() => adminApi.put("/settings", { door_photographs_media_id: p.media_id }), "Now shown on the homepage Photographs doorway")}>Use on homepage</button>}</div>
        )}
        {trim && p.media_id && <TrimPanel mediaId={p.media_id} initRotate={p.display_rotate || 0} initCrop={p.display_crop} onDone={() => setV(Date.now())} />}
      </div>
    </div>
  );
}

export default function PhotosTab() {
  const [albums, loadAlbums] = useAdmin("/c/albums");
  const [filter, setFilter] = useState("");
  const [photos, loadPhotos] = useAdmin(`/photos${filter ? `?status=${filter}` : ""}`);
  const [target, setTarget] = useState("");
  const [autoTrim, setAutoTrim] = useState(true);
  const addFamily = async (ms) => {
    await save(() => adminApi.post("/photos", { media_ids: ms.map((m) => m.id), album_id: target || null, auto_trim: autoTrim }), "Photographs added");
    loadPhotos();
  };
  return (
    <>
      <Albums albums={albums} reload={loadAlbums} />
      <Panel title="Photographs" testid="admin-photos"
        actions={<div className="flex flex-wrap items-center gap-2">
          <select className="lou-input !py-1.5 !w-auto text-sm" value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Album for new photographs">
            <option value="">Unfiled</option>{(albums || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
          <Check label="Auto-trim scanned edges" checked={autoTrim} onChange={setAutoTrim} testid="admin-photos-autotrim" />
          <Uploader multiple label="Add family photographs" onUploaded={addFamily} testid="admin-photos-upload" />
        </div>}>
        <div className="flex flex-wrap gap-2 mb-5">
          {["", "approved", "private", "pending", "rejected", "removed"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "bg-lou-ink text-white" : "border-lou-line"}`} data-testid={`admin-photos-filter-${s || "all"}`}>{s || "all"}</button>
          ))}
        </div>
        <div className="space-y-3">{(photos || []).map((p) => <PhotoRow key={p.id} p={p} albums={albums} reload={loadPhotos} />)}</div>
      </Panel>
    </>
  );
}
